/**
 * Admin live feed of site inspection submissions. Same data is the audit trail
 * for future Form 11 and site inspection report generation (see PROJECT_CONTEXT / DESIGN doc).
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import api, { getSiteInspectionPingUrl } from '../utils/api';
import { RefreshCw, MapPin, User, FileText } from 'lucide-react';
import { InfoButton } from '../components/InfoModal';
import PageHeading from '../components/PageHeading';

function parsePhotoUrls(row) {
  if (!row.photo_urls) return [];
  if (Array.isArray(row.photo_urls)) return row.photo_urls;
  try {
    const p = typeof row.photo_urls === 'string' ? JSON.parse(row.photo_urls) : row.photo_urls;
    return Array.isArray(p) ? p : [];
  } catch {
    return [];
  }
}

function photoSrc(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

export default function SiteInspectionFeed() {
  const [buildingId, setBuildingId] = useState('');
  const { data: buildingsData } = useQuery({
    queryKey: ['buildings', 'site-feed-filter'],
    queryFn: async () => {
      const res = await api.get('/buildings', { params: { page: 1, limit: 2000, search: '' } });
      return res.data?.data ?? [];
    },
    staleTime: 60_000,
    retry: false,
  });
  const buildingsList = Array.isArray(buildingsData) ? buildingsData : [];

  const { data, isLoading, refetch, isFetching, dataUpdatedAt, isError, error } = useQuery({
    queryKey: ['site-inspection-results', buildingId],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (buildingId) params.set('buildingId', buildingId);
      params.set('limit', '200');
      const res = await api.get(`/site-inspection-points/results?${params.toString()}`);
      return res.data?.data ?? [];
    },
    retry: (failureCount, err) => err?.response?.status === 404 ? false : failureCount < 2,
    refetchInterval: (query) => {
      const e = query.state.error;
      const st = e && typeof e === 'object' && 'response' in e ? e.response?.status : null;
      if (st === 404) return false;
      return 8000;
    },
  });
  const rows = data ?? [];
  const is404 = isError && error?.response?.status === 404;

  return (
    <div className="space-y-6">
      <PageHeading
        title="Site inspection"
        className="mb-0"
        infoTitle="Site inspection feed"
        infoChildren={
          <p>
            Live worker submissions (SS, situation, notes, photos). Polls every 8 seconds when the API is available.
            Retained for audit and future Form 11 / site inspection reporting.
          </p>
        }
      />

      <div className="flex flex-wrap items-center gap-4">
        <label className="text-sm text-gray-700 flex flex-col gap-1">
          <span>Filter by building</span>
          <select
            value={buildingId}
            onChange={(e) => setBuildingId(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 min-w-[14rem]"
          >
            <option value="">All buildings</option>
            {buildingsList.map((b) => (
              <option key={b.id} value={String(b.id)}>
                {(b.name || '').trim() || 'Building'}
                {(b.address || '').trim() ? ` — ${(b.address || '').trim().slice(0, 60)}` : ''}
              </option>
            ))}
          </select>
        </label>
        <button
          type="button"
          onClick={() => refetch()}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          <RefreshCw className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh now
        </button>
        {dataUpdatedAt && (
          <span className="text-xs text-gray-500">
            Last update: {new Date(dataUpdatedAt).toLocaleTimeString()}
          </span>
        )}
      </div>

      {isLoading && <p className="text-gray-500">Loading…</p>}

      {is404 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-sm text-amber-950 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-medium">Site inspection API returned 404.</span>
          <InfoButton title="Fixing 404 on site inspection API" label="Info">
            <p>
              Start the API from <code className="rounded bg-gray-100 px-1 text-xs">web/backend</code> with{' '}
              <code className="rounded bg-gray-100 px-1 text-xs">npm run dev</code> (MySQL must be running so the server
              can bind).
            </p>
            <p>
              Quick check:{' '}
              <code className="rounded bg-gray-100 px-1 text-xs break-all">{getSiteInspectionPingUrl()}</code> — expect{' '}
              <code className="rounded bg-gray-100 px-1 text-xs">{'{ "ok": true }'}</code>.
            </p>
          </InfoButton>
        </div>
      )}

      <div className="space-y-4">
        {rows.length === 0 && !isLoading && !is404 && (
          <div className="bg-white border border-gray-200 rounded-lg p-8 text-center text-gray-500">
            No site inspection submissions yet.
          </div>
        )}
        {rows.map((row) => {
          const photos = parsePhotoUrls(row);
          return (
            <article
              key={row.id}
              className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden"
            >
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex flex-wrap justify-between gap-2">
                <div className="flex items-center gap-2 text-sm">
                  <span className="font-semibold text-gray-900">#{row.id}</span>
                  <span className="text-primary-700 font-medium">{row.ss_code}</span>
                  <span className="text-gray-500">{row.situation_key}</span>
                </div>
                <time className="text-xs text-gray-500">
                  {row.created_at ? new Date(row.created_at).toLocaleString() : ''}
                </time>
              </div>
              <div className="p-4 space-y-3">
                <div className="flex flex-wrap gap-4 text-sm">
                  <span className="inline-flex items-center gap-1 text-gray-700">
                    <MapPin className="w-4 h-4 shrink-0" />
                    {row.building_name?.trim() || 'Building'}
                  </span>
                  <span className="inline-flex items-center gap-1 text-gray-700">
                    <User className="w-4 h-4 shrink-0" />
                    {row.worker_name || row.worker_id}
                  </span>
                  {row.job_id && (
                    <span className="text-gray-600">Inspection #{row.job_id}</span>
                  )}
                </div>
                <div>
                  <p className="text-xs font-medium text-gray-500 flex items-center gap-1">
                    <FileText className="w-3 h-3" />
                    {row.point_title || 'Checkpoint'}
                  </p>
                  {row.point_instruction && (
                    <p className="text-xs text-gray-500 mt-0.5">{row.point_instruction}</p>
                  )}
                  {row.note_text && (
                    <p className="text-gray-800 mt-2 whitespace-pre-wrap">{row.note_text}</p>
                  )}
                </div>
                {photos.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {photos.map((u, i) => (
                      <a
                        key={i}
                        href={photoSrc(u)}
                        target="_blank"
                        rel="noreferrer"
                        className="block"
                      >
                        <img
                          src={photoSrc(u)}
                          alt=""
                          className="h-32 w-auto rounded border border-gray-200 object-cover"
                        />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
